import { DragEndEvent } from "@dnd-kit/core";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  IconButton,
  Skeleton,
  Snackbar,
  TablePagination,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { useLocation } from "react-router-dom";

import { SortColumn, SortDirection } from "@/shared/domain/files/FileService";
import { FileSystemNode } from "@/shared/domain/files/filesystem";

import { FileBrowserAction } from "./actions";
import { DatasetFileGrid } from "./DatasetFileGrid";
import { DatasetFileTable } from "./DatasetFileTable";
import { FileBrowserDndContext } from "./FileBrowserDndContext";
import { NoFilesUploadedAlert } from "./NoFilesUploadedAlert";
import { PaginationConfig } from "./state";

interface SnackbarState {
  isOpen: boolean;
  message: string;
}

interface DatasetDisplaysProps {
  dispatch: React.Dispatch<FileBrowserAction>;
  displayMode: string;
  datasetId: string;
  tableConfig: PaginationConfig;
  totalItemCount: number | undefined;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => void;
  onRowsPerPageChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | undefined,
  ) => void;
  currentPage: FileSystemNode[] | undefined;
  isFetching: boolean;
  isPending: boolean;
  isPlaceholderData: boolean;
  isSearching: boolean;
  handleDeleteRow: (row: FileSystemNode) => void;
  handleRowDoubleClick: (row: FileSystemNode) => void;
  gridItems: number;
  selectedItems: FileSystemNode[];
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn, direction: SortDirection) => void;
  onValidDrop?: (event: DragEndEvent, fs_nodes: FileSystemNode[]) => void;
}

export const DatasetDisplays: React.FC<DatasetDisplaysProps> = ({
  dispatch,
  displayMode,
  datasetId,
  tableConfig,
  totalItemCount,
  onPageChange,
  onRowsPerPageChange,
  currentPage,
  isFetching,
  isPending,
  isPlaceholderData,
  isSearching,
  handleDeleteRow,
  handleRowDoubleClick,
  gridItems,
  selectedItems,
  handleActionConfirmation,
  handleSelectRows,
  sortColumn,
  sortDirection,
  onSort,
  onValidDrop,
}) => {
  const theme = useTheme();

  const { pathname } = useLocation();
  const cleanPathname = pathname.replace(/^\/+|\/+$/g, "");
  const inFolder = cleanPathname.split("/").length > 2;

  const [snackbarState, setSnackbarState] = React.useState<SnackbarState>({
    isOpen: false,
    message: "",
  });
  const [errorState, setErrorState] = React.useState<string | null>(null);

  const handleSnackbarClose = () => {
    setSnackbarState({
      isOpen: false,
      message: "",
    });
  };

  let content;
  if ((currentPage && currentPage.length > 0) || isSearching || inFolder) {
    content = (
      <FileBrowserDndContext
        dispatch={dispatch}
        selectedItems={selectedItems}
        onError={(message) => {
          setErrorState(message);
        }}
        onSuccess={(message) => {
          setSnackbarState({
            isOpen: true,
            message: message,
          });
        }}
        onValidDrop={(_event, nodesToMove) => {
          if (nodesToMove.length > 1) {
            setSnackbarState({
              isOpen: true,
              message: "Moving items...",
            });
          }
          onValidDrop?.(_event, nodesToMove);
        }}
      >
        {displayMode === "table" ? (
          <DatasetFileTable
            currentPage={currentPage ?? []}
            datasetId={datasetId}
            handleDeleteRow={handleDeleteRow}
            handleRowDoubleClick={handleRowDoubleClick}
            handleActionConfirmation={handleActionConfirmation}
            isFetching={isFetching}
            isPlaceholderData={isPlaceholderData}
            isSearching={isSearching}
            selectedItems={selectedItems}
            handleSelectRows={handleSelectRows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
          />
        ) : (
          <DatasetFileGrid
            datasetId={datasetId}
            currentPage={currentPage ?? []}
            handleRowClick={handleRowDoubleClick}
            isSearching={isSearching}
            gridItems={gridItems}
            selectedItems={selectedItems}
            handleSelectRows={handleSelectRows}
            emptyContent={<NoFilesUploadedAlert datasetId={datasetId} />}
          />
        )}
        <TablePagination
          component={Box}
          count={totalItemCount ?? -1}
          page={tableConfig.page}
          rowsPerPage={tableConfig.rowsPerPage}
          labelRowsPerPage={"Items per page:"}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(event, newPage) => {
            onPageChange(event, newPage);
          }}
          onRowsPerPageChange={(event) => {
            onRowsPerPageChange(event);
          }}
        />
      </FileBrowserDndContext>
    );
  } else {
    content = <NoFilesUploadedAlert datasetId={datasetId} />;
  }

  if (isPending) {
    return (
      <Box
        sx={{
          width: "100%",
          marginBottom: theme.spacing(1),
        }}
      >
        <Skeleton animation="wave" variant="rounded" height={150} />
      </Box>
    );
  }

  return (
    <>
      {errorState && (
        <Alert
          severity="error"
          sx={{ marginBottom: theme.spacing(2) }}
          onClose={() => setErrorState(null)}
        >
          {errorState}
        </Alert>
      )}
      {content}
      <Snackbar
        open={snackbarState.isOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarState.message}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleSnackbarClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </>
  );
};
